import {
  type GetWorkerRequirementRequest,
  type GetWorkerRequirementsResponse,
  getWorkerRequirementsResponseSchema,
} from "@clipboard-health/contract-worker-app-bff";
import {
  getPaginationNextPageContext,
  getPaginationNextPageParameters,
  type PaginationPageParameters,
} from "@src/appV2/api/pagination";
import {
  useInfiniteGetQuery,
  type UseInfiniteGetQueryOptions,
} from "@src/appV2/api/useInfiniteGetQuery";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { type InfiniteData } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { minutesToMilliseconds } from "date-fns";
import { isEmpty } from "lodash";

import { useDocumentExclusionFilter } from "../hooks/useDocumentExclusionFilter";
import { mergeWorkerRequirementWithIncludedResources } from "./mergeWorkerRequirementWithIncludedResources";
import { type WorkerRequirement } from "./types";

export const GET_WORKER_REQUIREMENTS_PATH = "/worker-requirements";
export const GET_WORKER_REQUIREMENTS_URL = `${environmentConfig.REACT_APP_WORKER_APP_BFF_URL}${GET_WORKER_REQUIREMENTS_PATH}`;

export function useGetPaginatedWorkerRequirements(
  queryParams: GetWorkerRequirementRequest,
  options: UseInfiniteGetQueryOptions<
    GetWorkerRequirementsResponse,
    AxiosError,
    WorkerRequirement[]
  > = {}
) {
  // Need to check for filter in queryParams for type safety
  const hasQualifications = "filter" in queryParams && !isEmpty(queryParams.filter.qualifications);
  const filterDocuments = useDocumentExclusionFilter();

  return useInfiniteGetQuery<
    GetWorkerRequirementRequest,
    GetWorkerRequirementsResponse,
    WorkerRequirement[],
    PaginationPageParameters
  >({
    url: GET_WORKER_REQUIREMENTS_URL,
    queryParams,
    responseSchema: getWorkerRequirementsResponseSchema,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_WORKER_REQUIREMENTS_FAILURE,
    },
    select: (data: InfiniteData<GetWorkerRequirementsResponse>) => {
      const mergedData = data.pages.flatMap((page) =>
        mergeWorkerRequirementWithIncludedResources(page)
      );
      return {
        pages: [filterDocuments(mergedData)],
        pageParams: data.pageParams,
      };
    },
    staleTime: options.staleTime ?? minutesToMilliseconds(5),
    nextPageContext: getPaginationNextPageContext,
    getNextPageParam: getPaginationNextPageParameters,
    ...options,
    enabled: hasQualifications && (options.enabled ?? true),
  });
}
